import {
  ChangeDetectorRef,
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { CadObject } from '../../../../models/cad-object.model';
import { PullDirection } from '../../../../models/pull-direction.model';
import { parseApiError } from '../../../quotes-api-error';
import { CadFileService } from '../../../services/cad-file.service';
import { MouldingGraphService } from '../../services/moulding-graph.service';
import { MouldingProcessingService } from '../../services/moulding-processing.service';
import { PullDirectionsComponent } from '../../pull-directions/pull-directions.component';
import { NodeRunStatus } from '../moulding-node-registry';

@Component({
  selector: 'app-pull-direction-node-config-dialog',
  standalone: true,
  imports: [DialogModule, ButtonModule, TooltipModule, PullDirectionsComponent],
  templateUrl: './pull-direction-node-config-dialog.component.html',
})
export class PullDirectionNodeConfigDialogComponent
  implements OnChanges, DoCheck
{
  private readonly graph = inject(MouldingGraphService);
  readonly processing = inject(MouldingProcessingService);
  private readonly cadFileService = inject(CadFileService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input({ required: true }) nodeId!: string;
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  // Starts true so the dialog's very first paint (as it fades in) already
  // shows the loading state, rather than a flash of stale/empty content
  // before `loadConnectedCad()` kicks in.
  loading = true;
  actionError: string | null = null;
  cadObjects: CadObject[] = [];
  pullDirections: PullDirection[] = [];

  // Draft selection, local to this dialog session. It is committed to the
  // node run only when the user clicks "Save".
  selectedPullDirection: PullDirection | null = null;
  findingPullDirection = false;
  savingCustomDirection = false;
  private lastKnownStatus: NodeRunStatus = 'idle';

  get status(): NodeRunStatus {
    return (
      this.graph.getNodeData(this.nodeId, 'pull_direction')?.status ?? 'idle'
    );
  }

  get runErrorMessage(): string | null {
    return (
      this.graph.getNodeData(this.nodeId, 'pull_direction')?.errorMessage ??
      null
    );
  }

  get primaryCadObjectId(): number | null {
    return (
      this.graph.getNodeData(this.nodeId, 'pull_direction')?.result?.artifact
        .primary_cad_object_id ?? null
    );
  }

  get cadFileId(): number | null {
    return (
      this.graph.getNodeData(this.nodeId, 'pull_direction')?.result?.artifact
        .cad_file_id ?? null
    );
  }

  get canFindPullDirection(): boolean {
    return (
      !this.loading &&
      !this.findingPullDirection &&
      !this.savingCustomDirection &&
      !this.processing.downstreamExecutionActive() &&
      this.status !== 'pending' &&
      this.status !== 'processing'
    );
  }

  // Trigger the load the moment `visible` flips to true (i.e. as soon as the
  // dialog starts opening), rather than waiting for the dialog's "shown"
  // event (which only fires after its open animation finishes). Loading
  // eagerly means the first frame already reflects the loading/empty state
  // instead of briefly showing stale content left over from the last time
  // this node's dialog was open.
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.selectedPullDirection = this.getSavedPullDirection();
      this.findingPullDirection = false;
      this.lastKnownStatus = this.status;
      void this.loadConnectedCad();
    }
  }

  onVisibleChange(visible: boolean): void {
    this.visible = visible;
    this.visibleChange.emit(visible);
  }

  onPullDirectionSelected(direction: PullDirection): void {
    this.selectedPullDirection = direction;
  }

  async onFindPullDirection(): Promise<void> {
    if (!this.canFindPullDirection) {
      return;
    }
    this.findingPullDirection = true;
    this.selectedPullDirection = null;
    this.actionError = null;
    try {
      await this.processing.startProcessingNode(
        this.nodeId,
        'pull_direction',
        false,
      );
      this.lastKnownStatus = this.status;
    } catch (error) {
      this.actionError = parseApiError(
        error,
        'Failed to find a pull direction.',
      );
      this.findingPullDirection = false;
    }
  }

  ngDoCheck(): void {
    const currentStatus = this.status;
    if (currentStatus === this.lastKnownStatus) {
      return;
    }
    const wasBusy =
      this.lastKnownStatus === 'pending' ||
      this.lastKnownStatus === 'processing';
    this.lastKnownStatus = currentStatus;
    if (
      wasBusy &&
      (currentStatus === 'complete' || currentStatus === 'error')
    ) {
      const result = this.graph.getNodeData(
        this.nodeId,
        'pull_direction',
      )?.result;
      if (currentStatus === 'complete' && result) {
        this.pullDirections = result.pull_directions;
        this.selectedPullDirection =
          result.artifact.pull_direction ?? result.pull_directions[0] ?? null;
      }
      this.findingPullDirection = false;
    }
  }

  async onSave(): Promise<void> {
    const data = this.graph.getNodeData(this.nodeId, 'pull_direction');
    if (data?.result === null) {
      this.savingCustomDirection = true;
      this.actionError = null;
      try {
        await this.processing.startProcessingNode(
          this.nodeId,
          'pull_direction',
          false,
        );
      } catch (error) {
        this.actionError = parseApiError(
          error,
          'Failed to create the CAD artifact.',
        );
        return;
      } finally {
        this.savingCustomDirection = false;
      }
    } else if (this.selectedPullDirection) {
      this.savingCustomDirection = true;
      this.actionError = null;
      try {
        await this.processing.setPullDirection(
          this.nodeId,
          this.selectedPullDirection,
        );
      } catch (error) {
        this.actionError = parseApiError(
          error,
          'Failed to save the pull direction.',
        );
        return;
      } finally {
        this.savingCustomDirection = false;
      }
    }
    this.onVisibleChange(false);
  }

  onClose(): void {
    this.onVisibleChange(false);
  }

  private async loadConnectedCad(): Promise<void> {
    this.loading = true;
    this.cadObjects = [];
    this.pullDirections = [];

    const result =
      this.graph.getNodeData(this.nodeId, 'pull_direction')?.result ?? null;
    if (result === null) {
      this.loading = false;
      this.cdr.detectChanges();
      return;
    }
    this.pullDirections = result.pull_directions;

    try {
      this.cadObjects = await firstValueFrom(
        this.cadFileService.loadCadObjects([
          result.artifact.primary_cad_object_id,
        ]),
      );
    } catch {
      // Leave the viewer empty when the CAD cannot be loaded.
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  private getSavedPullDirection(): PullDirection | null {
    return (
      this.graph.getNodeData(this.nodeId, 'pull_direction')?.result?.artifact
        .pull_direction ?? null
    );
  }
}
